import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AZURE_MJ } from "@/lib/axios";
import {
  ICentralProjectsSummaryRes,
  IForecastingCentralRepoRes,
  IAllForecastingRes,
  IForecastingRes,
  IForecastingVersionRes,
  ICreateMonthlyForecastPayload,
  IUpdateMonthlyForecastPayload,
  IBulkUpdateTasksPayload,
  IQaStatusItem
} from "@/types/forecasting";

export const FORECASTING_KEYS = {
  getCentralProjectsSummary: "FORECASTING_GET_CENTRAL_PROJECTS_SUMMARY",
  getProjectCentralRepo: "FORECASTING_GET_PROJECT_CENTRAL_REPO",
  getProjectCentralRepoWithOpportunityNo:
    "FORECASTING_GET_PROJECT_CENTRAL_REPO_WITH_OPPORTUNITY_NO",
  getMonthlyForecast: "FORECASTING_GET_MONTHLY_FORECAST",
  getAllMonthlyForecasts: "FORECASTING_GET_ALL_MONTHLY_FORECASTS",
  getMonthlyForecastVersions: "FORECASTTING_GET_MONTHLY_FORECAST_VERSIONS",
  getAllQaStatusItemsForProject:
    "FORECASTING_GET_ALL_QA_STATUS_ITEMS_FOR_PROJECT"
};

export const useGetCentralProjectsSummary = () => {
  return useQuery({
    queryKey: [FORECASTING_KEYS.getCentralProjectsSummary],
    queryFn: async (): Promise<ICentralProjectsSummaryRes> => {
      const { data } = await AZURE_MJ.get(`/v3/central/projects/summary`);
      return data;
    }
  });
};

export const useGetProjectCentralRepo = (project?: string) => {
  return useQuery({
    queryKey: [FORECASTING_KEYS.getProjectCentralRepo, project],
    queryFn: async (): Promise<IForecastingCentralRepoRes> => {
      const { data } = await AZURE_MJ.get(
        `/v3/central/projects/${encodeURIComponent(project!)}`
      );
      return data;
    },
    enabled: !!project
  });
};

export const useGetProjectCentralRepoWithOpportunityNo = (
  opprotunityNo?: string
) => {
  return useQuery({
    queryKey: [
      FORECASTING_KEYS.getProjectCentralRepoWithOpportunityNo,
      opprotunityNo
    ],
    queryFn: async (): Promise<IForecastingCentralRepoRes> => {
      const { data } = await AZURE_MJ.get(
        `/v3/central/projects/opportunity/${opprotunityNo}`
      );
      return data;
    },
    enabled: !!opprotunityNo
  });
};

/**
 * GET /v3/forecasts/{projectId}/{year}/{month}?opportunityNo={opportunityNo}
 *
 * `projectId`/`projectName` stays in the path; `opportunityNo` is an optional
 * query param. When passed and found by the backend, the path projectId is
 * ignored for the lookup. Omitting it keeps the legacy project-name behaviour.
 */
export const useGetMonthlyForecast = (
  project?: string,
  year?: string,
  month?: string,
  opportunityNo?: string
) => {
  return useQuery({
    queryKey: [
      FORECASTING_KEYS.getMonthlyForecast,
      project,
      year,
      month,
      opportunityNo
    ],
    queryFn: async (): Promise<IForecastingRes> => {
      const { data } = await AZURE_MJ.get(
        `/v3/forecasts/${encodeURIComponent(project!)}/${year}/${month}`,
        { params: opportunityNo ? { opportunityNo } : undefined }
      );
      return data;
    },
    enabled: Boolean(project && year && month)
  });
};

/**
 * Endpoint 2 — GET /v3/forecasts/opportunity/{opportunityNo}/all
 *
 * Migrated from the legacy `/v2/forecasts/{projectName}/all` route. The
 * forecast is now keyed by `opportunityNo` in the path; `projectName` is still
 * sent as a query param (`?projectName=`) for backwards compatibility while the
 * backend completes its migration.
 */
export const useGetAllMonthlyForecasts = (
  opportunityNo?: string,
  projectName?: string
) => {
  return useQuery({
    queryKey: [
      FORECASTING_KEYS.getAllMonthlyForecasts,
      opportunityNo,
      projectName
    ],
    queryFn: async (): Promise<IAllForecastingRes> => {
      const { data } = await AZURE_MJ.get(
        `/v3/forecasts/opportunity/${encodeURIComponent(opportunityNo || "0")}/all`,
        { params: { projectName } }
      );
      return data;
    },
    enabled: !!opportunityNo
  });
};

/**
 * GET /v3/forecasts/{projectId}/{month}/versions?opportunityNo={opportunityNo}
 *
 * `projectId` stays in the path; `opportunityNo` is an optional query param.
 * When passed and found, the path projectId is ignored for the lookup.
 */
export const useGetMonthlyForecastVersions = (
  project?: string,
  yearMonth?: string,
  opportunityNo?: string
) => {
  return useQuery({
    queryKey: [
      FORECASTING_KEYS.getMonthlyForecastVersions,
      project,
      yearMonth,
      opportunityNo
    ],
    queryFn: async (): Promise<{
      projectId: string;
      month: string;
      totalVersions: number;
      versions: IForecastingVersionRes[];
    }> => {
      const { data } = await AZURE_MJ.get(
        `/v3/forecasts/${project}/${yearMonth}/versions`,
        { params: opportunityNo ? { opportunityNo } : undefined }
      );
      return data;
    },
    enabled: Boolean(project && yearMonth)
  });
};

export const useGetAllQaStatusItemsForProject = (
  opportunityNo?: string | null
) => {
  return useQuery({
    queryKey: [FORECASTING_KEYS.getAllQaStatusItemsForProject, opportunityNo],
    queryFn: async (): Promise<{
      TotalItems: number;
      QAItems: IQaStatusItem[];
    }> => {
      const { data } = await AZURE_MJ.get(
        `/v3/central/qa-status/all?opportunityNo=${encodeURIComponent(opportunityNo!)}`
      );
      return data;
    },
    enabled: !!opportunityNo
  });
};

/**
 * Endpoint 1 — POST /v3/forecasts
 *
 * Creates the first forecast for a project/month. The URL is unchanged by the
 * opportunityNo migration: both `projectId` and `projectName` are already part
 * of the request payload (`ICreateMonthlyForecastPayload`), which is what the
 * backend uses for backwards compatibility.
 */
export const useCreateMonthlyForecast = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ICreateMonthlyForecastPayload) => {
      const { data } = await AZURE_MJ.post("/v3/forecasts", payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FORECASTING_KEYS.getMonthlyForecast]
      });
    }
  });
};

export const useUpdateMonthlyForecast = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      project: string;
      date: string;
      opportunityNo?: string;
      payload: IUpdateMonthlyForecastPayload;
    }) => {
      // PATCH /v3/forecasts/{projectId}/{month}?opportunityNo={opportunityNo}
      // `opportunityNo` is an optional query param; when found the path
      // projectId is ignored for the lookup. Body is unchanged.
      const res = await AZURE_MJ.patch(
        `/v3/forecasts/${encodeURIComponent(data.project)}/${data.date}`,
        data.payload,
        {
          params: data.opportunityNo
            ? { opportunityNo: data.opportunityNo }
            : undefined
        }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FORECASTING_KEYS.getMonthlyForecast]
      });
    }
  });
};

/**
 * Endpoint 3 — POST /v3/forecasts/opportunity/{opportunityNo}/{month}/new-version
 *
 * Migrated from the legacy `/v2/forecasts/{projectName}/{month}/new-version`
 * route. Keyed by `opportunityNo` in the path; `projectName` is sent in the
 * request payload for backwards compatibility (backend resolution order:
 * opportunityNo → projectName). When `opportunityNo` is missing, `"0"` is sent
 * in the path.
 */
export const useCreateNewMonthlyForecastVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      opportunityNo?: string;
      projectName: string;
      date: string;
      payload: any;
    }) => {
      const res = await AZURE_MJ.post(
        `/v3/forecasts/opportunity/${encodeURIComponent(data.opportunityNo || "0")}/${data.date}/new-version`,
        { projectName: data.projectName, ...data.payload }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FORECASTING_KEYS.getMonthlyForecast]
      });
    }
  });
};

export const useUpdateQaStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      project: string;
      date: string;
      opportunityNo?: string;
      payload: {
        jobType: string;
        plot: string;
        stage: string;
        qaStatus: string;
        updatedBy: string;
      };
    }) => {
      // PATCH /v3/forecasts/{projectId}/{month}/qa-status?opportunityNo={opportunityNo}
      // `opportunityNo` is an optional query param; when found the path
      // projectId is ignored for the lookup. Body is unchanged.
      const res = await AZURE_MJ.patch(
        `/v3/forecasts/${encodeURIComponent(data.project)}/${data.date}/qa-status`,
        data.payload,
        {
          params: data.opportunityNo
            ? { opportunityNo: data.opportunityNo }
            : undefined
        }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FORECASTING_KEYS.getAllQaStatusItemsForProject]
      });
    }
  });
};

/**
 * Endpoint 4 — PATCH /v3/forecasts/opportunity/{opportunityNo}/{month}/lock
 *
 * Migrated from the legacy `/v2/forecasts/{projectName}/{month}/lock` route.
 * Keyed by `opportunityNo` in the path; `projectName` is sent in the request
 * payload for backwards compatibility. When `opportunityNo` is missing, `"0"`
 * is sent in the path.
 */
export const useLockUnlockForecast = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      opportunityNo?: string;
      projectName: string;
      date: string;
      payload: {
        isLocked: boolean;
        lockedBy: string;
        lockReason: string;
      };
    }) => {
      const res = await AZURE_MJ.patch(
        `/v3/forecasts/opportunity/${encodeURIComponent(data.opportunityNo || "0")}/${data.date}/lock`,
        { projectName: data.projectName, ...data.payload }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FORECASTING_KEYS.getMonthlyForecast]
      });
    }
  });
};

export const useBulkUpdateTasks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      project: string;
      payload: IBulkUpdateTasksPayload;
    }) => {
      const res = await AZURE_MJ.patch(
        `/v3/central/projects/${encodeURIComponent(data.project)}/tasks/bulk-update`,
        data.payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FORECASTING_KEYS.getProjectCentralRepo]
      });
    }
  });
};
